import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import api from '../services/api';
import { 
  History, 
  Clock, 
  User, 
  Calendar, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Play,
  Pause,
  XCircle,
  FolderOpen,
  Settings,
  Plus,
  Trash2
} from 'lucide-react';

interface ProjectActionHistory {
  id: number;
  project: string;
  action_type: string;
  action_type_display: string;
  description: string;
  old_value?: string;
  new_value?: string;
  performed_by: number;
  performed_by_name: string;
  created_at: string;
}

const HistoriquePage: React.FC = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState<ProjectActionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/projects-management/history/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      setHistory(data.history);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique:', err);
      setError('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const refreshHistory = async () => {
    try {
      setRefreshing(true);
      await loadHistory();
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.isAdmin) {
      loadHistory();
    }
  }, [user?.isAdmin]);

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'created': return <Plus className="w-4 h-4 text-blue-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'reopened': return <Play className="w-4 h-4 text-blue-500" />;
      case 'status_changed': return <Settings className="w-4 h-4 text-orange-500" />;
      case 'step_created': return <Plus className="w-4 h-4 text-purple-500" />;
      case 'step_updated': return <Settings className="w-4 h-4 text-yellow-500" />;
      case 'step_deleted': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'service_added': return <Plus className="w-4 h-4 text-indigo-500" />;
      default: return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé</h1>
          <p className="text-gray-600">Cette page est réservée aux administrateurs.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadHistory}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <History className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-900">Historique des Projets</h1>
            </div>
            <button
              onClick={refreshHistory}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
          <p className="text-gray-600">
            Suivi complet de toutes les actions effectuées sur les projets
          </p>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <History className="w-8 h-8 text-indigo-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Actions</p>
                <p className="text-2xl font-bold text-gray-900">{history.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Projets Terminés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {history.filter(h => h.action_type === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Play className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Projets Réouverts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {history.filter(h => h.action_type === 'reopened').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Plus className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Étapes Créées</p>
                <p className="text-2xl font-bold text-gray-900">
                  {history.filter(h => h.action_type === 'step_created').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste de l'historique */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Historique des Actions</h2>
          </div>
          
          {history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun historique</h3>
              <p className="text-gray-600">Les actions sur les projets apparaîtront ici.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {history.map((action) => (
                <div key={action.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getActionIcon(action.action_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FolderOpen className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            Projet #{action.project}
                          </span>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                            {action.action_type_display}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>{action.performed_by_name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDate(action.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-700 mb-2">{action.description}</p>
                      
                      {(action.old_value || action.new_value) && (
                        <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                          {action.old_value && (
                            <div>
                              <span className="font-medium">Ancien :</span> {action.old_value}
                            </div>
                          )}
                          {action.new_value && (
                            <div>
                              <span className="font-medium">Nouveau :</span> {action.new_value}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoriquePage;
